import { useState } from 'react';
import { categories } from '@/mocks/categories';

const column1 = categories.slice(0, 5);
const column2 = categories.slice(5, 10);
const column3 = categories.slice(10, 14);

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setSubmitStatus('success');
      setSubmitMessage('Thank you for subscribing!');
      setEmail('');
      return;
    }
    setSubmitStatus('loading');
    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d97lua2g3iubr452i560', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
      if (response.ok && parsed?.code === 'OK') {
        setSubmitStatus('success');
        setSubmitMessage('Thank you for subscribing!');
        setEmail('');
      } else {
        if (typeof serverMsg === 'string' && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setSubmitStatus('error');
          setSubmitMessage('Submission failed. Please try again.');
        } else {
          setSubmitStatus('error');
          setSubmitMessage(typeof serverMsg === 'string' ? serverMsg : 'Something went wrong. Please try again.');
        }
      }
    } catch {
      setSubmitStatus('error');
      setSubmitMessage('Network error. Please try again.');
    }
  };

  return (
    <footer className="bg-background-100 border-t border-background-200">
      <div className="w-full px-4 md:px-6 py-14 md:py-16">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          <div className="lg:w-[38%]">
            <h3 className="font-heading text-3xl md:text-4xl font-bold text-primary-500 leading-tight">
              Kakobuy
            </h3>
            <h3 className="font-heading text-3xl md:text-4xl font-semibold text-foreground-600 leading-tight">
              Finds.casa
            </h3>
            <p className="mt-4 text-sm text-foreground-600 max-w-xs leading-relaxed">
              An independent research platform providing educational resources about product spreadsheets. We are not affiliated with Kakobuy, CSSBuy, Superbuy, or Oopbuy.
            </p>

            <div className="mt-8">
              <p className="text-xs font-medium text-foreground-500 uppercase tracking-wider mb-3">Get Research Alerts</p>
              <form data-readdy-form onSubmit={handleSubscribe} className="relative">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="w-full bg-transparent border-b border-foreground-300 py-2.5 pr-20 text-sm text-foreground-800 placeholder-foreground-400 outline-none focus:border-primary-500 transition-colors"
                />
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="newsletter-honeypot"
                />
                <button
                  type="submit"
                  disabled={submitStatus === 'loading'}
                  className="absolute right-0 top-1/2 -translate-y-1/2 px-4 py-1.5 rounded-full border border-foreground-300 text-xs text-foreground-600 hover:border-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {submitStatus === 'loading' ? 'Sending...' : 'Subscribe'}
                </button>
              </form>
              {submitStatus === 'success' && (
                <p className="mt-2 text-xs text-accent-600">{submitMessage}</p>
              )}
              {submitStatus === 'error' && (
                <p className="mt-2 text-xs text-red-500">{submitMessage}</p>
              )}
            </div>
          </div>

          <div className="lg:w-[62%] grid grid-cols-2 sm:grid-cols-3 gap-8">
            <div>
              <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-4">Categories</p>
              <ul className="space-y-2.5">
                {column1.map((cat) => (
                  <li key={cat.slug}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-4">&nbsp;</p>
              <ul className="space-y-2.5">
                {column2.map((cat) => (
                  <li key={cat.slug}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-4">&nbsp;</p>
              <ul className="space-y-2.5">
                {column3.map((cat) => (
                  <li key={cat.slug}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-background-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-500">
            &copy; {new Date().getFullYear()} Kakobuy Spreadsheet Finds. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a href="/kakobuy-spreadsheet-guide" className="text-xs text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer">Kakobuy Guide</a>
            <a href="/cssbuy-spreadsheet" className="text-xs text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer">CSSBuy Guide</a>
            <a href="/qc-spreadsheet" className="text-xs text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer">QC Guide</a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-xs text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer">Main Site</a>
          </div>
        </div>
      </div>
    </footer>
  );
}